/**
 * InlineHost — `IEditorHost` 의 inline 모드 구현 (PR-N1).
 *
 * NgwEditor 는 iframe 을 띄우지 않는 React 컴포넌트라 `mode: 'inline'` 만 지원한다.
 * 본 클래스는 어댑터가 `createRoot(container).render(<Editor />)` 로 마운트한 mount element
 * 의 document / window / mountElement 를 통일된 표면으로 노출한다.
 *
 * ### IframeHost 와의 차이
 *
 * - iframe 폴링 / `?hash=` cache busting / contentWindow null 처리가 모두 불필요 (inline 은
 *   동기적으로 부모 document/window 그대로 사용).
 * - `teardown()` 은 React root unmount 가 어댑터 `lifecycle.dispose()` 책임이라 본 클래스는
 *   container 안 DOM 정리만 수행 (mount element 추적용).
 * - `mountElement` 는 어댑터가 init 시 `EditorHandle.getEditorDOM()` 으로 받아 setMountElement
 *   로 주입한다 (lazy). NgwEditor 가 React 렌더 후 ProseMirror view.dom 을 노출하기 전까지는
 *   container 자체를 반환한다.
 *
 * ### PR-10 (TinyMCE inline 모드) 대비 일반화
 *
 * 본 InlineHost 는 NgwEditor 전용 폴더(`ngweditor/`) 에 위치하지만 표면 자체는 에디터
 * 비종속이다. PR-10-spike 가 TinyMCE inline 모드의 일반성을 실측한 뒤
 * `packages/libs/src/editor/InlineHost.ts` 로 이전 검토 (현 시점 결정 보류 — `ngweditor/`
 * 위치 유지).
 *
 * **timer context**: 본 클래스는 폴링/타이머를 사용하지 않는다 — inline 마운트는 동기적이다.
 */

import type { EditorHostMode, IEditorHost } from '../IEditorHost'

export interface InlineHostOptions {
  /** 에디터가 마운트될 컨테이너 (어댑터가 `createRoot(container).render(<Editor />)`) */
  container: HTMLElement
}

export class InlineHost implements IEditorHost {
  readonly mode: EditorHostMode = 'inline'

  private container: HTMLElement | null
  private mountElement: HTMLElement | null = null

  constructor(opts: InlineHostOptions) {
    this.container = opts.container
  }

  getDocument(): Document {
    if (this.container == null) {
      throw new Error('InlineHost: torn down')
    }
    return this.container.ownerDocument
  }

  getWindow(): Window {
    if (this.container == null) {
      throw new Error('InlineHost: torn down')
    }
    const win = this.container.ownerDocument.defaultView
    if (win == null) {
      throw new Error('InlineHost: defaultView null (detached document)')
    }
    return win
  }

  /**
   * 실제 에디터가 마운트되는 요소.
   *
   * NgwEditor 의 경우 `EditorHandle.getEditorDOM()` 으로 ProseMirror view.dom 을 반환한다.
   * 어댑터가 `onReady` 발화 후 `setMountElement(handle.getEditorDOM())` 로 주입한다.
   * 주입 전(또는 핸들이 view.dom 미준비 단계) 에는 container 자체를 반환 — 호출자가 inner
   * 요소 접근을 시도하면 빈 컨테이너지만 throw 하지 않아 race 가 안전.
   */
  getMountElement(): HTMLElement {
    if (this.mountElement != null) {
      return this.mountElement
    }
    if (this.container == null) {
      throw new Error('InlineHost: torn down')
    }
    return this.container
  }

  /**
   * 어댑터의 `lifecycle.init` 이 `EditorHandle.getEditorDOM()` 결과를 주입.
   * `null` 호출은 reset 용도 (dispose 직전).
   * @internal
   */
  setMountElement(el: HTMLElement | null): void {
    this.mountElement = el
  }

  /**
   * 호스트 윈도우 자체에 포커스. iframe 과 달리 대부분 no-op (parent window 동일).
   * NgwEditor 의 selection 포커스는 `cursor.focus()` (PR-N2) 로 별도 처리.
   */
  focus(): void {
    if (this.container == null) {
      return
    }
    this.container.ownerDocument.defaultView?.focus()
  }

  /**
   * 어댑터 `lifecycle.dispose()` 가 React root.unmount() 를 먼저 수행한 뒤 본 메서드를 호출.
   * 본 클래스는 mount element 참조 해제 + container 참조 해제만 담당.
   */
  teardown(): void {
    this.mountElement = null
    this.container = null
  }
}
